import { IconAutoRenew, IconErrorOutline, IconOutlineInfo, IconPower, IconPowerOff } from "@/assets/icons";
import { ItemInfo } from "@/components/commons/ItemInfo";
import { Button } from "@/components/elements/Button";
import { InnerBox } from "@/components/elements/InnerBox";
import { Tooltip } from "@/components/elements/Tooltip";
import { CompanyFormTitle } from "@/features/company/components/company-info/CompanyFormTitle";
import { EMPTY_STR } from "@/constants/string";
import { black, orange, text } from "@/theme/colors";
import { spacing } from "@/theme/variables";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { FC } from "react";

export type TVisaActionType = "link" | "unlink" | "sync" | "cancel";

export type TVisaCountItem = {
  label: string;
  value?: string;
  tooltip?: string;
};

type TVisaLinkSectionProps = {
  title: string;
  description: string;
  statusValue: string;
  lastSyncDate?: string | null;
  visaCount: TVisaCountItem;
  studyCount: TVisaCountItem;
  note: string;
  isLinked?: boolean;
  isEditMode?: boolean;
  activeAction?: TVisaActionType | null;
  // 認証待ち (pending) の申請が存在する場合。セットアップの代わりに「連携申請を取り消す」ボタンを表示する。
  isPendingApproval?: boolean;
  onUnlink: () => void;
  onSync: () => void;
  onSetup: () => void;
  // 認証待ち申請の取り消し。isPendingApproval のとき使用する。
  onCancelRequest?: () => void;
};

export const VisaLinkSection: FC<TVisaLinkSectionProps> = ({
  title,
  description,
  statusValue,
  lastSyncDate,
  visaCount,
  studyCount,
  note,
  isLinked = false,
  isEditMode = false,
  activeAction = null,
  isPendingApproval = false,
  onUnlink,
  onSync,
  onSetup,
  onCancelRequest,
}) => {
  const setupButton = (
    <Button
      variant="primary"
      size="sm"
      startIcon={<IconPower fontSize={20} />}
      onClick={onSetup}
      isLoading={activeAction === "link"}
      disabled={isEditMode || activeAction !== null}
    >
      連携をセットアップ
    </Button>
  );

  return (
    <InnerBox
      title={<CompanyFormTitle text={title} />}
      showDivider={false}
      borderColor={orange[80]}
      gap={spacing.md} // タイトルと説明文の間隔はデザイン指定で24px
      rightContent={
        <Stack direction="row" gap={2}>
          {isLinked ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                startIcon={<IconPowerOff fontSize={20} />}
                onClick={onUnlink}
                isLoading={activeAction === "unlink"}
                disabled={isEditMode || activeAction !== null}
              >
                連携を解除する
              </Button>
              <Button
                variant="outline"
                size="sm"
                startIcon={<IconAutoRenew fontSize={20} />}
                onClick={onSync}
                isLoading={activeAction === "sync"}
                disabled={isEditMode || activeAction !== null}
              >
                irohana visa 同期
              </Button>
            </>
          ) : isPendingApproval ? (
            // 認証待ちの間はセットアップを無効化する代わりに、申請を取り消せるボタンを表示する。
            <Button
              variant="ghost"
              size="sm"
              startIcon={<IconPowerOff fontSize={20} />}
              onClick={onCancelRequest}
              isLoading={activeAction === "cancel"}
              disabled={isEditMode || activeAction !== null}
            >
              連携申請を取り消す
            </Button>
          ) : (
            setupButton
          )}
        </Stack>
      }
    >
      <Stack gap={2}>
        <Typography variant="cap12" color={text.primary}>
          {description}
        </Typography>
        <Grid container rowSpacing={2} columnSpacing={3}>
          <Grid item xs={6}>
            <ItemInfo label="連携ステータス" value={statusValue} />
          </Grid>
          <Grid item xs={6}>
            <ItemInfo label="最終同期日" value={lastSyncDate ?? EMPTY_STR.DATE} type="date" />
          </Grid>
          <Grid item xs={6}>
            <CountItemInfo item={visaCount} />
          </Grid>
          <Grid item xs={6}>
            <CountItemInfo item={studyCount} />
          </Grid>
        </Grid>
        <Typography variant="cap12" color={text.secondary}>
          <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconErrorOutline fontSize="20px" color={black[80]} />
            {note}
          </Box>
        </Typography>
      </Stack>
    </InnerBox>
  );
};

const CountItemInfo: FC<{ item: TVisaCountItem }> = ({ item }) => (
  <Stack gap={1}>
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Typography variant="cap12" color={black[70]}>
        {item.label}
      </Typography>
      {item.tooltip && (
        <Tooltip title="" content={item.tooltip}>
          <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle", lineHeight: 0, cursor: "pointer" }}>
            <IconOutlineInfo width={15} height={15} color={black[80]} />
          </Box>
        </Tooltip>
      )}
    </Stack>
    <Typography variant="body14Semi" color={text.primary}>
      {item.value != null ? `${item.value} 名` : EMPTY_STR.TEXT}
    </Typography>
  </Stack>
);
